import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Cpu, Search } from 'lucide-react';
import { useDeviceList } from '../api/queries';
import { fmtRelative } from '../lib/format';
import { useIsMasked } from './auth';
import { NAV } from './nav';

export type CommandPaletteHandle = { open: () => void; toggle: () => void };

type Item = { group: string; title: string; desc: string; href: string; icon: ReactNode; end?: string };

/**
 * ⌘K 命令面板（DESIGN.md §4.18）：输 SN（后 4 位也行）或机主直达设备，也能跳页面。
 * 它是第二入口，不放导航里没有的功能。设备搜索复用设备列表接口，和列表页的搜索同一套匹配。
 */
const CommandPalette = forwardRef<CommandPaletteHandle>(function CommandPalette(_, ref) {
  const navigate = useNavigate();
  // 履约、员工看不到机主，也不能按机主找（sidecar device_list_svc）：提示里不提机主
  const masked = useIsMasked();
  const dlg = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [sel, setSel] = useState(0);

  const open = () => {
    const d = dlg.current;
    if (!d || d.open) return;
    setQ('');
    setDebounced('');
    setSel(0);
    d.showModal();
    input.current?.focus();
  };
  useImperativeHandle(ref, () => ({
    open,
    toggle: () => (dlg.current?.open ? dlg.current.close() : open()),
  }));

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  // 少于 3 个字符不查：SN 后 4 位是最短的有效输入，再短会匹配出一大片
  const term = debounced.length >= 3 ? debounced : '';
  const { data, isFetching } = useDeviceList(
    { page: 1, pageSize: 6, sortBy: 'lastActive', sortDir: 'desc', search: term },
    term.length > 0,
  );

  const lower = q.trim().toLowerCase();
  const pages: Item[] = NAV.filter((n) => !lower || n.label.toLowerCase().includes(lower) || n.hint.toLowerCase().includes(lower)).map((n) => {
    const Icon = n.icon;
    return { group: '页面', title: n.label, desc: n.hint, href: n.href, icon: <Icon className="ico" aria-hidden="true" />, end: '页面' };
  });
  // SN 以输入结尾的排最前：输「后 4 位」是最常见的找法
  const tail = term.toLowerCase();
  const found = term && data ? [...data.items].sort((a, b) => Number(b.sn.toLowerCase().endsWith(tail)) - Number(a.sn.toLowerCase().endsWith(tail))) : [];
  const devices: Item[] = found.map((d) => ({
    group: '设备',
    title: d.sn,
    desc: [d.status === 'online' ? '在线' : '离线', d.owner, d.lastActive ? `最近 ${fmtRelative(d.lastActive)}` : null].filter(Boolean).join(' · '),
    href: `/devices/${encodeURIComponent(d.sn)}`,
    icon: <Cpu className="ico" aria-hidden="true" />,
    end: '设备',
  }));
  const items = [...devices, ...pages];
  const current = Math.min(sel, Math.max(0, items.length - 1));

  const go = (it: Item | undefined) => {
    if (!it) return;
    dlg.current?.close();
    navigate(it.href);
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSel(Math.min(current + 1, items.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSel(Math.max(current - 1, 0));
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      go(items[current]);
    }
  };

  let lastGroup = '';
  return (
    // 点遮罩关闭是鼠标的便利；键盘有 Esc（<dialog> 自带）
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={dlg}
      className="cmdk"
      aria-label="搜索与跳转"
      onClick={(e) => {
        if (e.target === dlg.current) dlg.current?.close();
      }}
    >
      <div className="cmdk-in">
        <Search className="ico" aria-hidden="true" />
        <input
          ref={input}
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setSel(0);
          }}
          onKeyDown={onKey}
          placeholder={masked ? 'SN（后 4 位也行），或页面' : 'SN（后 4 位也行）、机主，或页面'}
          aria-label="搜索"
          aria-controls="cmdk-list"
          autoComplete="off"
        />
        <span className="kbd">Esc</span>
      </div>
      <div className="cmdk-list" id="cmdk-list" role="listbox" aria-label="搜索结果">
        {items.length === 0 ? (
          <div className="cmdk-empty">
            {term && isFetching
              ? '正在查找设备…'
              : `没有找到「${q.trim()}」。${masked ? '可以输 SN 或 SN 后 4 位。' : '可以输 SN、SN 后 4 位或机主。'}`}
          </div>
        ) : (
          items.map((it, i) => {
            const head = it.group !== lastGroup ? <div className="cmdk-g">{it.group}</div> : null;
            lastGroup = it.group;
            return (
              <div key={`${it.group}-${it.href}`}>
                {head}
                <a
                  className="cmdk-i"
                  href={it.href}
                  role="option"
                  aria-selected={i === current}
                  onMouseMove={() => {
                    if (i !== current) setSel(i);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    go(it);
                  }}
                >
                  {it.icon}
                  <div className="cmdk-txt">
                    <div className="t">{it.title}</div>
                    <div className="d">{it.desc}</div>
                  </div>
                  {it.end && <span className="end">{it.end}</span>}
                </a>
              </div>
            );
          })
        )}
      </div>
      <div className="cmdk-f">
        <span><span className="kbd">↑</span> <span className="kbd">↓</span> 选择</span>
        <span><span className="kbd">↵</span> 打开</span>
        <span><span className="kbd">Esc</span> 关闭</span>
      </div>
    </dialog>
  );
});

export default CommandPalette;
